/**
 * Продаюче вікно доступу (owner 2026-09-09).
 *
 * Сюди людина потрапляє з кінця онбордингу («Подивитись вакансії») або коли
 * відкриває вакансію в застосунку, не підписавшись на канал.
 *
 * Що тут має статись, дослівно від власника: «щоб відкрити доступ до бота,
 * бачити й переглядати всі вакансії, треба підписатись на наш канал. Типу для
 * тебе це десять секунд, для нас це підтримка нашого бренду, ми за це
 * відкриваємо доступ до бота».
 *
 * Тобто це не стіна і не копія екрана підбірки, а чесно названий обмін —
 * видно, що просимо і що даємо. Саме обмін тут і продає.
 *
 * ⚠️ Формулювання: «доступ до бота», НЕ «повний доступ». «Повний доступ» —
 * назва платного тарифу, і обіцяти його за підписку на канал не можна.
 *
 * ⏱️ ЗВОРОТНИЙ ЗВʼЯЗОК МУСИТЬ БУТИ МИТТЄВИЙ (owner 2026-09-09: «підписався,
 * повернувся — ніхуя, стою 15 секунд»). Дві причини мовчання:
 *   1) `/me` кешується на 30 секунд і сам не перечитується при поверненні;
 *   2) перша спроба покладалась на подію видимості й на лічильник у `ref` —
 *      але Telegram при поверненні з каналу перезапускає застосунок, тобто
 *      компонент монтується заново, `ref` обнуляється, а подія видимості так
 *      і не приходить. Виходило, що не опитував ніхто.
 *
 * Тому зараз просто: поки цей екран відкритий, кожні три секунди питаємо
 * Telegram напряму (`check-channel-subscription`, він же ставить прапорець).
 * Жодних умов, які можуть не спрацювати. Перша перевірка — одразу на монтуванні.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ActionButton } from "@/components/ActionButton";
import { BackNav } from "@/components/BackNav";
import { Icon } from "@/components/Icon";
import { PageHeader } from "@/components/PageHeader";
import { Section } from "@/components/Section";
import { apiFetch } from "@/api/client";
import { queryKeys, useMe } from "@/api/hooks";
import { openTelegramChat } from "@/lib/openExternal";
import { tg } from "@/lib/telegram";

const CHANNEL_URL = "https://t.me/inflow_freelance";
/** Поки екран відкритий — питаємо ось так часто. */
const POLL_EVERY_MS = 3000;
/** І не довше цього: людина могла просто лишити застосунок відкритим. */
const POLL_FOR_MS = 5 * 60_000;

/** Рядок обміну: плитка 10×10 зліва, текст по центру плитки. */
function DealRow({
  icon,
  accent,
  children,
  first,
}: {
  icon: "clock" | "flow";
  accent?: boolean;
  children: React.ReactNode;
  first?: boolean;
}) {
  return (
    <div
      className={
        "flex items-center gap-3 p-4 " +
        (first ? "" : "border-t border-ink/[0.06]")
      }
    >
      <span
        className={
          "grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] " +
          (accent ? "bg-primary/10 text-primary" : "bg-ink/[0.05] text-ink-dim")
        }
      >
        <Icon name={icon} size={16} />
      </span>
      <p className="text-[0.875rem] leading-snug text-ink [text-wrap:balance]">
        {children}
      </p>
    </div>
  );
}

export function UnlockPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const [checking, setChecking] = useState(false);
  const busyRef = useRef(false);

  // Прапорець зʼявився — ведемо далі самі, нічого натискати не треба.
  useEffect(() => {
    if (me.data?.selection_unlocked) {
      tg()?.HapticFeedback?.notificationOccurred?.("success");
      navigate("/feed", { replace: true });
    }
  }, [me.data?.selection_unlocked, navigate]);

  /** Спитати Telegram напряму. Він же проставить прапорець, якщо підписана. */
  const check = useCallback(
    async (showSpinner: boolean) => {
      if (busyRef.current) return;
      busyRef.current = true;
      if (showSpinner) setChecking(true);
      try {
        const r = await apiFetch<{ subscribed: boolean; message: string }>(
          "/onboarding/check-channel-subscription",
          { method: "POST", body: {} },
        );
        if (r.subscribed) {
          await qc.invalidateQueries({ queryKey: queryKeys.me });
        }
      } catch {
        // Мовчки: це фонова перевірка, помилка тут людині нічого не каже.
      } finally {
        busyRef.current = false;
        if (showSpinner) setChecking(false);
      }
    },
    [qc],
  );

  // Опитуємо весь час, поки екран відкритий. Без умов і без ref-лічильників:
  // вони не переживають перезапуск застосунку при поверненні з каналу.
  useEffect(() => {
    check(false);
    const started = Date.now();
    const id = setInterval(() => {
      if (Date.now() - started > POLL_FOR_MS) {
        clearInterval(id);
        return;
      }
      check(false);
    }, POLL_EVERY_MS);
    return () => clearInterval(id);
  }, [check]);

  // Повернулась у застосунок — перевіряємо негайно, не чекаючи такту.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") check(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [check]);

  return (
    <>
      <BackNav showMenu />

      <PageHeader
        eyebrow="залишився один крок"
        title="Відкрий доступ"
        titleLead="Відкрий"
        titleAccent="доступ."
        scribbleWidth={132}
        subtitle="Щоб отримувати вакансії й переглядати їх у застосунку, підпишись на наш канал."
      />

      <Section title="Що це означає">
        <DealRow icon="clock" first>
          Тобі це <b>десять секунд</b>.
        </DealRow>
        <DealRow icon="flow" accent>
          Нам це <b>підтримка бренду</b> — і за це ми відкриваємо тобі доступ до
          бота.
        </DealRow>
      </Section>

      <ActionButton
        disabled={checking}
        onClick={() => {
          tg()?.HapticFeedback?.impactOccurred?.("light");
          openTelegramChat(CHANNEL_URL);
        }}
      >
        {checking ? "Перевіряємо підписку…" : "Підписатися на канал"}
      </ActionButton>

      <p className="pt-2.5 text-center text-[0.78125rem] leading-relaxed text-ink-dim [text-wrap:balance]">
        {checking
          ? "Це займе пару секунд."
          : "Доступ відкриється одразу після підписки."}
      </p>
    </>
  );
}
